import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "./ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";
import {
    Upload,
    Trash2,
    Download,
    Loader2,
    FileText,
    Plus,
    Files,
    KeyRound,
    CheckCircle2,
    ArrowRightLeft,
    Search,
    ShieldCheck,
    History,
    FileDown,
    Eye,
    EyeOff,
    Archive,
    PenLine,
    Sparkles,
    Pencil,
    Wand2,
    Copy,
    Mail,
} from "lucide-react";
import { CategoryIcon } from "../lib/fileIcons";
import { generateDeliveryReceiptPDF } from "../lib/deliveryReceipt";
import { loadSettings } from "../lib/settings";
import { useCompany } from "../contexts/CompanyContext";
import { DocumentPreviewDialog } from "./DocumentPreviewDialog";
import { DownloadStatusBadge } from "./DownloadStatusBadge";
import {
    CATEGORY_LABELS,
    CATEGORY_ORDER,
    downloadDocument,
    formatBytes,
    formatDateTime,
} from "../lib/documents";
import { toast } from "sonner";
import { DatePicker } from "./DatePicker";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortableHead } from "./SortableHead";
import { Checkbox } from "./ui/checkbox";

// Decodifica gli errori di upload in messaggi user-friendly e persistenti.
// Restituisce { message, duration } pronti per `toast.error(message, { duration })`.
function decodeUploadError(error) {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const detailStr = typeof detail === "string" ? detail : "";

    // Storage Emergent esaurito (402 Payment Required dall'object storage)
    if (status === 503 && /402|Payment Required/i.test(detailStr)) {
        return {
            message:
                "🛑 Spazio storage esaurito: il credito dell'Emergent Object Storage è terminato. " +
                "Apri Profilo → Universal Key → Aggiungi credito (o attiva auto-ricarica) per ripristinare gli upload.",
            duration: 10000,
        };
    }
    // Servizio storage temporaneamente non disponibile
    if (status === 503) {
        return {
            message:
                "Storage temporaneamente non disponibile. Riprova tra qualche secondo. " +
                (detailStr ? `Dettaglio: ${detailStr}` : ""),
            duration: 6000,
        };
    }
    // File troppo grande (413 Payload Too Large)
    if (status === 413) {
        return {
            message:
                "File troppo grande. Riduci la dimensione del PDF (es. comprimi le immagini) e riprova.",
            duration: 6000,
        };
    }
    // Auth scaduta
    if (status === 401) {
        return {
            message: "Sessione scaduta. Effettua di nuovo il login e riprova.",
            duration: 6000,
        };
    }
    // Categoria non valida o dati mancanti (422)
    if (status === 422) {
        return {
            message: `Dati non validi: ${detailStr || "controlla i campi obbligatori"}.`,
            duration: 6000,
        };
    }
    // Generico
    const msg = detailStr || error?.message || "Errore upload";
    return { message: msg, duration: 6000 };
}


export const EmployeeDocumentsPanel = forwardRef(function EmployeeDocumentsPanel({ employee, categoryFilter, onCategoryFilterChange, actionsAtTop = false }, ref) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [credsOpen, setCredsOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [reassignDoc, setReassignDoc] = useState(null);
    const [historyDoc, setHistoryDoc] = useState(null);
    const [togglingId, setTogglingId] = useState(null);
    const [visTogglingId, setVisTogglingId] = useState(null);
    const [paperTogglingId, setPaperTogglingId] = useState(null);
    const [editDoc, setEditDoc] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/documents/by-employee/${employee.id}`);
            setDocs(data);
        } catch (e) {
            console.error("Errore caricamento documenti dipendente:", e);
            toast.error("Errore nel caricamento documenti");
        } finally {
            setLoading(false);
        }
    }, [employee.id]);

    // Expose imperative handles so the parent (EmployeeDetail) can open the
    // dialogs from the top actions row.
    useImperativeHandle(ref, () => ({
        openUpload: () => setUploadOpen(true),
        openBulk: () => setBulkOpen(true),
        openCredentials: () => setCredsOpen(true),
        refresh: () => fetch(),
    }), [fetch]);

    // Filter docs by selected category (controlled from parent via clickable widgets)
    const filteredDocs = useMemo(() => {
        if (!categoryFilter || categoryFilter === "all") return docs;
        return docs.filter((d) => d.category === categoryFilter);
    }, [docs, categoryFilter]);

    // Per-category counts for the filter chips
    const countsByCategory = useMemo(() => {
        const m = {};
        for (const d of docs) m[d.category] = (m[d.category] || 0) + 1;
        return m;
    }, [docs]);

    const sortedCategories = useMemo(
        () => Object.entries(countsByCategory).sort((a, b) => b[1] - a[1]),
        [countsByCategory]
    );

    const { sortedItems: filteredDocsSorted, sortKey, sortDir, requestSort } = useSortableTable(filteredDocs, {
        customAccessors: {
            categoria: (d) => d.category || "",
            documento: (d) => (d.filename || d.title || "").toLowerCase(),
            periodo: (d) => d.period_label || d.period_from || "",
        },
    });

    useEffect(() => {
        fetch();
    }, [fetch]);

    const handleDownload = async (doc) => {
        setDownloadingId(doc.id);
        try {
            await downloadDocument(doc.id, doc.filename);
            toast.success("Documento scaricato");
        } catch (e) {
            toast.error("Errore nel download");
        } finally {
            setDownloadingId(null);
        }
    };

    useEffect(() => {
        setSelectedIds(new Set());
    }, [categoryFilter]);

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/documents/${confirmDelete.id}`);
            toast.success("Documento eliminato");
            setDocs((arr) => arr.filter((d) => d.id !== confirmDelete.id));
        } catch (e) {
            toast.error("Errore eliminazione");
        } finally {
            setConfirmDelete(null);
        }
    };

    const toggleSelect = (docId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(docId)) next.delete(docId);
            else next.add(docId);
            return next;
        });
    };

    const handleBulkDelete = async () => {
        const ids = [...selectedIds];
        if (!ids.length) return;
        setBulkDeleting(true);
        try {
            const { data } = await api.post("/documents/bulk-delete", { document_ids: ids });
            toast.success(`${data.deleted} ${data.deleted === 1 ? "documento eliminato" : "documenti eliminati"}`);
            setDocs((arr) => arr.filter((d) => !selectedIds.has(d.id)));
            setSelectedIds(new Set());
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore eliminazione multipla");
        } finally {
            setBulkDeleting(false);
            setConfirmBulkOpen(false);
        }
    };

    const [bulkNotifying, setBulkNotifying] = useState(false);

    const handleBulkNotify = async () => {
        const ids = [...selectedIds];
        if (!ids.length) return;
        setBulkNotifying(true);
        try {
            const { data } = await api.post("/documents/notify", { document_ids: ids });
            toast.success(`Notifiche email in invio per ${data.queued} ${data.queued === 1 ? "documento" : "documenti"}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore invio notifiche");
        } finally {
            setBulkNotifying(false);
        }
    };

    const handleBulkFlags = async (flags, msg) => {
        const ids = [...selectedIds];
        if (!ids.length) return;
        setBulkDeleting(true);
        try {
            const { data } = await api.post("/documents/bulk-update-flags", { document_ids: ids, ...flags });
            setDocs((arr) => arr.map((d) => (selectedIds.has(d.id) ? { ...d, ...flags } : d)));
            toast.success(`${data.updated} ${data.updated === 1 ? "documento aggiornato" : "documenti aggiornati"}: ${msg}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore aggiornamento multiplo");
        } finally {
            setBulkDeleting(false);
        }
    };

    const [aiBulkRunning, setAiBulkRunning] = useState(false);

    const runAiExpiry = async () => {
        const ids = [...selectedIds];
        if (!ids.length) return;
        setAiBulkRunning(true);
        try {
            const { data } = await api.post("/documents/extract-expiry-batch", { document_ids: ids });
            if (!data.total) {
                toast.info("Nessun documento idoneo (categorie: visita medica, certificato, documento identità — PDF/JPG/PNG)");
                return;
            }
            toast.info(`Analisi AI avviata su ${data.total} ${data.total === 1 ? "documento" : "documenti"}…`);
            let job = data;
            for (let i = 0; i < 400 && job.status !== "done"; i++) {
                await new Promise((r) => setTimeout(r, 2500));
                job = (await api.get(`/documents/extract-expiry-jobs/${data.job_id}`)).data;
            }
            toast.success(`Scadenze AI: ${job.updated} compilate, ${job.not_found} senza data nel file${job.errors ? `, ${job.errors} errori` : ""}`);
            fetch();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore analisi AI");
        } finally {
            setAiBulkRunning(false);
        }
    };

    const toggleAckRequired = async (doc) => {
        setTogglingId(doc.id);
        const next = !doc.requires_acknowledgment;
        try {
            await api.patch(`/documents/${doc.id}`, { requires_acknowledgment: next });
            setDocs((arr) => arr.map((d) => d.id === doc.id ? { ...d, requires_acknowledgment: next } : d));
            toast.success(next ? "Richiesta presa visione attivata" : "Richiesta presa visione disattivata");
        } catch (e) {
            toast.error("Errore aggiornamento");
        } finally {
            setTogglingId(null);
        }
    };

    const toggleEmployeeVisibility = async (doc) => {
        setVisTogglingId(doc.id);
        const nextHidden = !doc.hidden_from_employee;
        try {
            await api.patch(`/documents/${doc.id}`, { hidden_from_employee: nextHidden });
            setDocs((arr) => arr.map((d) => d.id === doc.id ? { ...d, hidden_from_employee: nextHidden } : d));
            toast.success(nextHidden ? "Documento nascosto al dipendente" : "Documento ora visibile al dipendente");
        } catch (e) {
            toast.error("Errore aggiornamento");
        } finally {
            setVisTogglingId(null);
        }
    };

    const toggleSignedOnPaper = async (doc) => {
        setPaperTogglingId(doc.id);
        const next = !doc.signed_on_paper;
        try {
            await api.patch(`/documents/${doc.id}`, { signed_on_paper: next });
            setDocs((arr) => arr.map((d) => d.id === doc.id ? { ...d, signed_on_paper: next } : d));
            toast.success(next ? "Contrassegnato: firmato in originale cartaceo" : "Contrassegno 'originale cartaceo' rimosso");
        } catch (e) {
            toast.error("Errore aggiornamento");
        } finally {
            setPaperTogglingId(null);
        }
    };

    return (
        <div className="space-y-4" data-testid="admin-documents-panel">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h3 className="font-display text-base font-bold text-[#376DB3]">Documenti dipendente</h3>
                    <p className="text-xs text-slate-500">
                        Buste paga, CU, contratti, certificati e comunicazioni assegnati a questo dipendente.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {!actionsAtTop && (
                        <>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCredsOpen(true)}
                                className="gap-2"
                                data-testid="open-credentials-button"
                            >
                                <KeyRound className="h-4 w-4" />
                                Credenziali
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setBulkOpen(true)}
                                className="gap-2"
                                data-testid="open-bulk-upload"
                            >
                                <Files className="h-4 w-4" />
                                Caricamento massivo
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => setUploadOpen(true)}
                                className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                data-testid="open-upload-document"
                            >
                                <Plus className="h-4 w-4" />
                                Carica documento
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Filter chips by category */}
            {docs.length > 0 && (
                <div className="-mt-3 flex flex-wrap items-center gap-2" data-testid="docs-category-chips">
                    <button
                        type="button"
                        onClick={() => onCategoryFilterChange?.("all")}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${(!categoryFilter || categoryFilter === "all") ? "border-[#376DB3] bg-[#376DB3] text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
                        data-testid="chip-all"
                    >
                        Tutti <span className="font-mono-numbers opacity-80">{docs.length}</span>
                    </button>
                    {sortedCategories.map(([cat, count]) => {
                        const active = categoryFilter === cat;
                        return (
                            <button
                                key={cat}
                                type="button"
                                onClick={() => onCategoryFilterChange?.(active ? "all" : cat)}
                                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active ? "border-[#376DB3] bg-[#376DB3] text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
                                data-testid={`chip-${cat}`}
                            >
                                {CATEGORY_LABELS[cat] || cat} <span className="font-mono-numbers opacity-80">{count}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {selectedIds.size > 0 && (
                    <div className="flex flex-wrap items-center gap-2 border-b border-[#376DB3]/20 bg-[#5BAEDB]/10 px-4 py-2" data-testid="docs-bulk-delete-bar">
                        <span className="mr-1 text-sm font-medium text-[#15498F]">
                            {selectedIds.size} {selectedIds.size === 1 ? "selezionato" : "selezionati"}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={bulkDeleting}
                            onClick={() => handleBulkFlags({ hidden_from_employee: true }, "nascosti al dipendente")}
                            className="h-7 gap-1.5 bg-white px-2.5 text-xs"
                            data-testid="docs-bulk-hide-btn"
                        >
                            <EyeOff className="h-3.5 w-3.5" />
                            Nascondi
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={bulkDeleting}
                            onClick={() => handleBulkFlags({ hidden_from_employee: false }, "visibili al dipendente")}
                            className="h-7 gap-1.5 bg-white px-2.5 text-xs"
                            data-testid="docs-bulk-show-btn"
                        >
                            <Eye className="h-3.5 w-3.5" />
                            Mostra
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={bulkDeleting}
                            onClick={() => handleBulkFlags({ requires_acknowledgment: true }, "presa visione richiesta")}
                            className="h-7 gap-1.5 bg-white px-2.5 text-xs"
                            data-testid="docs-bulk-ack-on-btn"
                        >
                            <ShieldCheck className="h-3.5 w-3.5 text-amber-600" />
                            Richiedi presa visione
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={bulkDeleting}
                            onClick={() => handleBulkFlags({ requires_acknowledgment: false }, "presa visione non richiesta")}
                            className="h-7 gap-1.5 bg-white px-2.5 text-xs"
                            data-testid="docs-bulk-ack-off-btn"
                        >
                            <ShieldCheck className="h-3.5 w-3.5 text-slate-400" />
                            Annulla presa visione
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={bulkDeleting || aiBulkRunning}
                            onClick={runAiExpiry}
                            className="h-7 gap-1.5 border-violet-200 bg-violet-50 px-2.5 text-xs text-violet-700 hover:bg-violet-100"
                            data-testid="docs-bulk-ai-expiry-btn"
                        >
                            {aiBulkRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                            {aiBulkRunning ? "Analisi…" : "Scadenza AI"}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={bulkDeleting}
                            onClick={() => handleBulkFlags({ signed_on_paper: true }, "firmato in originale cartaceo")}
                            className="h-7 gap-1.5 bg-white px-2.5 text-xs"
                            data-testid="docs-bulk-paper-on-btn"
                        >
                            <PenLine className="h-3.5 w-3.5 text-indigo-600" />
                            Orig. cartaceo
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={bulkDeleting}
                            onClick={() => handleBulkFlags({ signed_on_paper: false }, "contrassegno originale cartaceo rimosso")}
                            className="h-7 gap-1.5 bg-white px-2.5 text-xs"
                            data-testid="docs-bulk-paper-off-btn"
                        >
                            <PenLine className="h-3.5 w-3.5 text-slate-400" />
                            Annulla orig. cartaceo
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleBulkNotify}
                            disabled={bulkNotifying}
                            className="h-7 gap-1.5 bg-white px-2.5 text-xs"
                            data-testid="docs-bulk-notify-btn"
                            title="Invia ai dipendenti l'email 'nuovo documento disponibile' per i documenti selezionati"
                        >
                            {bulkNotifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5 text-[#376DB3]" />}
                            Invia notifica email
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => setConfirmBulkOpen(true)}
                            disabled={bulkDeleting}
                            className="h-7 gap-1.5 bg-red-600 px-2.5 text-xs text-white hover:bg-red-700"
                            data-testid="docs-bulk-delete-btn"
                        >
                            {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            Elimina
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedIds(new Set())}
                            className="h-7 px-2 text-xs text-slate-600"
                            data-testid="docs-bulk-clear-btn"
                        >
                            Deseleziona
                        </Button>
                    </div>
                )}
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                            <TableHead className="w-10">
                                <Checkbox
                                    checked={filteredDocsSorted.length > 0 && filteredDocsSorted.every((d) => selectedIds.has(d.id))
                                        ? true
                                        : selectedIds.size > 0
                                          ? "indeterminate"
                                          : false}
                                    onCheckedChange={(v) => setSelectedIds(v ? new Set(filteredDocsSorted.map((d) => d.id)) : new Set())}
                                    disabled={filteredDocsSorted.length === 0}
                                    data-testid="docs-select-all"
                                />
                            </TableHead>
                            <SortableHead sortKey="categoria" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                Categoria
                            </SortableHead>
                            <SortableHead sortKey="documento" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                Documento
                            </SortableHead>
                            <SortableHead sortKey="periodo" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="hidden md:table-cell">
                                Periodo
                            </SortableHead>
                            <SortableHead sortKey="uploaded_at" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="hidden lg:table-cell">
                                Caricato
                            </SortableHead>
                            <SortableHead sortKey="size_bytes" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="hidden sm:table-cell">
                                Dim.
                            </SortableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} className="py-10 text-center">
                                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
                                </TableCell>
                            </TableRow>
                        ) : filteredDocsSorted.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} className="py-10 text-center text-sm text-slate-500">
                                    <FileText className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                                    {docs.length === 0 ? "Nessun documento caricato." : `Nessun documento nella categoria "${CATEGORY_LABELS[categoryFilter] || categoryFilter}".`}
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredDocsSorted.map((doc) => (
                                <TableRow key={doc.id} data-testid={`admin-doc-row-${doc.id}`}>
                                    <TableCell>
                                        <Checkbox
                                            checked={selectedIds.has(doc.id)}
                                            onCheckedChange={() => toggleSelect(doc.id)}
                                            data-testid={`docs-select-${doc.id}`}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="secondary">{doc.category_label}</Badge>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <CategoryIcon category={doc.category} label={CATEGORY_LABELS[doc.category]} />
                                            <button
                                                type="button"
                                                onClick={() => setPreviewDoc(doc)}
                                                className="text-left font-medium text-[#376DB3] hover:underline"
                                                title="Anteprima documento"
                                                data-testid={`admin-preview-name-${doc.id}`}
                                            >
                                                {doc.title || doc.filename}
                                            </button>
                                            <DownloadStatusBadge
                                                downloadCount={doc.download_count}
                                                lastDownloadedAt={doc.last_downloaded_at}
                                                acknowledged={doc.acknowledged}
                                                legacyDownload={doc.legacy_download}
                                                testId={`admin-download-status-${doc.id}`}
                                            />
                                            {doc.requires_acknowledgment && (
                                                doc.acknowledged ? (
                                                    <Badge variant="outline" className="gap-1 border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px]" data-testid={`admin-ack-badge-${doc.id}`} title="Presa visione registrata dal dipendente">
                                                        <ShieldCheck className="h-3 w-3" />
                                                        Presa visione ✓
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 text-amber-700 text-[10px]" data-testid={`admin-ack-badge-${doc.id}`} title="Presa visione richiesta — il dipendente non l'ha ancora fatta">
                                                        <ShieldCheck className="h-3 w-3" />
                                                        Presa visione richiesta
                                                    </Badge>
                                                )
                                            )}
                                            {doc.hidden_from_employee && (
                                                <Badge variant="outline" className="gap-1 border-slate-300 bg-slate-100 text-slate-600 text-[10px]" data-testid={`admin-hidden-badge-${doc.id}`} title="Questo documento NON è visibile al dipendente nel suo portale">
                                                    <EyeOff className="h-3 w-3" />
                                                    Solo admin
                                                </Badge>
                                            )}
                                            {doc.signed_on_paper && (
                                                <Badge variant="outline" className="gap-1 border-indigo-300 bg-indigo-50 text-indigo-700 text-[10px]" data-testid={`admin-paper-badge-${doc.id}`} title="L'originale firmato è cartaceo — questo file non è la scansione firmata">
                                                    <PenLine className="h-3 w-3" />
                                                    Firmato in orig. cartaceo
                                                </Badge>
                                            )}
                                        </div>
                                        {doc.title && (
                                            <button
                                                type="button"
                                                onClick={() => setPreviewDoc(doc)}
                                                className="ml-9 text-left text-xs text-slate-500 hover:text-[#376DB3] hover:underline"
                                                title="Anteprima documento"
                                            >
                                                {doc.filename}
                                            </button>
                                        )}
                                    </TableCell>
                                    <TableCell className="hidden md:table-cell font-mono-numbers text-slate-700">
                                        {doc.period || "—"}
                                    </TableCell>
                                    <TableCell className="hidden lg:table-cell text-slate-600 text-xs">
                                        {formatDateTime(doc.uploaded_at)}
                                    </TableCell>
                                    <TableCell className="hidden sm:table-cell text-slate-600 font-mono-numbers text-xs">
                                        {formatBytes(doc.size_bytes)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="ml-auto grid w-fit grid-cols-4 gap-1">
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                title="Scarica"
                                                onClick={() => handleDownload(doc)}
                                                disabled={downloadingId === doc.id}
                                                data-testid={`admin-download-${doc.id}`}
                                            >
                                                {downloadingId === doc.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Download className="h-4 w-4" />
                                                )}
                                            </Button>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                title="Riassegna ad altro dipendente"
                                                onClick={() => setReassignDoc(doc)}
                                                data-testid={`admin-reassign-${doc.id}`}
                                                className="text-amber-600 hover:bg-amber-50 hover:text-amber-700"
                                            >
                                                <ArrowRightLeft className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                title={doc.requires_acknowledgment ? "Disattiva richiesta presa visione" : "Richiedi presa visione al dipendente"}
                                                onClick={() => toggleAckRequired(doc)}
                                                disabled={togglingId === doc.id}
                                                data-testid={`admin-toggle-ack-${doc.id}`}
                                                className={doc.requires_acknowledgment ? "text-amber-600 hover:bg-amber-50 hover:text-amber-700" : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"}
                                            >
                                                {togglingId === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                                            </Button>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                title={doc.hidden_from_employee ? "Rendi visibile al dipendente" : "Nascondi al dipendente (solo admin)"}
                                                onClick={() => toggleEmployeeVisibility(doc)}
                                                disabled={visTogglingId === doc.id}
                                                data-testid={`admin-toggle-visibility-${doc.id}`}
                                                className={doc.hidden_from_employee ? "text-slate-400 hover:bg-slate-100 hover:text-slate-600" : "text-sky-600 hover:bg-sky-50 hover:text-sky-700"}
                                            >
                                                {visTogglingId === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (doc.hidden_from_employee ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />)}
                                            </Button>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                title="Modifica titolo, categoria e scadenza"
                                                onClick={() => setEditDoc(doc)}
                                                data-testid={`admin-edit-doc-${doc.id}`}
                                                className="text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                title={doc.signed_on_paper ? "Rimuovi contrassegno 'firmato in originale cartaceo'" : "Contrassegna: firmato in originale cartaceo (non scansionato)"}
                                                onClick={() => toggleSignedOnPaper(doc)}
                                                disabled={paperTogglingId === doc.id}
                                                data-testid={`admin-toggle-paper-${doc.id}`}
                                                className={doc.signed_on_paper ? "text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700" : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"}
                                            >
                                                {paperTogglingId === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
                                            </Button>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                title="Storico download"
                                                onClick={() => setHistoryDoc(doc)}
                                                data-testid={`admin-history-${doc.id}`}
                                                className="text-violet-600 hover:bg-violet-50 hover:text-violet-700"
                                            >
                                                <History className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                title="Elimina"
                                                onClick={() => setConfirmDelete(doc)}
                                                data-testid={`admin-delete-${doc.id}`}
                                                className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <UploadDialog
                open={uploadOpen}
                onOpenChange={setUploadOpen}
                employee={employee}
                onUploaded={fetch}
            />
            <EditDocDialog
                doc={editDoc}
                employee={employee}
                onClose={() => setEditDoc(null)}
                onSaved={(updated) => {
                    setDocs((arr) => arr.map((d) => (d.id === updated.id ? updated : d)));
                    setEditDoc(null);
                }}
            />
            <BulkUploadDialog
                open={bulkOpen}
                onOpenChange={setBulkOpen}
                onUploaded={fetch}
            />
            <CredentialsDialog
                open={credsOpen}
                onOpenChange={setCredsOpen}
                employee={employee}
            />

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent data-testid="confirm-delete-doc">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare il documento?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmDelete && `"${confirmDelete.title || confirmDelete.filename}" verrà eliminato. Il file rimane in archivio ma non sarà più visibile al dipendente.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                            Elimina
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={confirmBulkOpen} onOpenChange={(o) => !o && setConfirmBulkOpen(false)}>
                <AlertDialogContent data-testid="confirm-bulk-delete-docs">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare {selectedIds.size} {selectedIds.size === 1 ? "documento" : "documenti"}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            I documenti selezionati verranno eliminati. I file restano in archivio ma non saranno più visibili al dipendente.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-bulk-delete-action">
                            Elimina tutti
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <ReassignDocumentDialog
                doc={reassignDoc}
                onClose={() => setReassignDoc(null)}
                onReassigned={async () => {
                    setReassignDoc(null);
                    await fetch();
                }}
            />

            <DownloadHistoryDialog
                doc={historyDoc}
                employee={employee}
                onClose={() => setHistoryDoc(null)}
            />
            <DocumentPreviewDialog
                open={!!previewDoc}
                document={previewDoc}
                onClose={() => setPreviewDoc(null)}
            />
        </div>
    );
});

const cleanTitleClient = (filename, employee) => {
    let stem = (filename || "").replace(/\.[^.]+$/, "").replace(/_+/g, " ").trim();
    const tokens = `${employee?.cognome || ""} ${employee?.nome || ""}`.toLowerCase().split(/\s+/).filter(Boolean);
    let changed = true;
    while (changed) {
        changed = false;
        for (const t of tokens) {
            const re = new RegExp("^" + t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "[\\s\\-_.,]*", "i");
            if (re.test(stem)) {
                stem = stem.replace(re, "");
                changed = true;
            }
        }
    }
    return stem.replace(/^[\s\-_.,]+/, "").replace(/[\s\-_.,]+$/, "").trim() || (filename || "");
};

// ---------------------------------------------------------------------------
// Edit document meta (title / category / expiry)
// ---------------------------------------------------------------------------
const EditDocDialog = ({ doc, employee, onClose, onSaved }) => {
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("altro");
    const [expiresAt, setExpiresAt] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (doc) {
            setTitle(doc.title || "");
            setCategory(doc.category || "altro");
            setExpiresAt(doc.expires_at || "");
        }
    }, [doc]);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/documents/${doc.id}`, {
                title: title.trim() || null,
                category,
                expires_at: expiresAt || null,
            });
            toast.success("Documento aggiornato");
            onSaved(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="edit-doc-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-slate-800">Modifica documento</DialogTitle>
                    <DialogDescription className="truncate">{doc?.filename}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Titolo</Label>
                        <div className="mt-1 flex gap-2">
                            <Input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Titolo del documento"
                                data-testid="edit-doc-title"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-9 whitespace-nowrap px-2 text-xs"
                                onClick={() => setTitle(cleanTitleClient(doc?.filename, employee))}
                                title="Rigenera il titolo dal nome del file"
                                data-testid="edit-doc-autotitle"
                            >
                                Auto
                            </Button>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Categoria</Label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700"
                            data-testid="edit-doc-category"
                        >
                            {CATEGORY_ORDER.map((c) => (
                                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Scadenza (opzionale)</Label>
                        <div className="mt-1">
                            <DatePicker
                                value={expiresAt}
                                onChange={setExpiresAt}
                                placeholder="Nessuna scadenza"
                                fromYear={new Date().getFullYear() - 5}
                                toYear={new Date().getFullYear() + 15}
                                data-testid="edit-doc-expiry"
                            />
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={onClose}>Annulla</Button>
                    <Button onClick={save} disabled={saving} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="edit-doc-save">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Salva
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const CAN_AI_EXPIRY = ["visita_medica", "certificato", "documento_identita"];

// ---------------------------------------------------------------------------
// Upload single document dialog
// ---------------------------------------------------------------------------
const UploadDialog = ({ open, onOpenChange, employee, onUploaded }) => {
    const [file, setFile] = useState(null);
    const [category, setCategory] = useState("busta_paga");
    const [title, setTitle] = useState("");
    const [period, setPeriod] = useState("");
    const [expiresAt, setExpiresAt] = useState("");
    const [signedOnPaper, setSignedOnPaper] = useState(false);
    const [reminderEnabled, setReminderEnabled] = useState(false);
    const [reminderDays, setReminderDays] = useState(7);
    const [requireAck, setRequireAck] = useState(false);
    const [ackCategories, setAckCategories] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [aiDetecting, setAiDetecting] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        api.get("/delivery/settings")
            .then((r) => {
                const cats = r.data.ack_categories || [];
                setAckCategories(cats);
                setRequireAck(cats.includes("busta_paga"));
            })
            .catch(() => {});
    }, [open]);

    const handleCategoryChange = (c) => {
        setCategory(c);
        setRequireAck(ackCategories.includes(c));
    };

    const detectExpiry = async () => {
        if (!file) {
            toast.error("Seleziona prima il file");
            return;
        }
        setAiDetecting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/documents/extract-expiry", fd, { headers: { "Content-Type": "multipart/form-data" } });
            if (data.found) {
                setExpiresAt(data.expires_at);
                const tipo = (data.tipo_documento || "").replaceAll("_", " ");
                toast.success(`Scadenza rilevata: ${data.expires_at.split("-").reverse().join("/")}${tipo ? ` (${tipo})` : ""}`);
            } else {
                toast.info("Nessuna data di scadenza trovata nel documento");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore analisi AI");
        } finally {
            setAiDetecting(false);
        }
    };

    const reset = () => {
        setFile(null);
        setCategory("busta_paga");
        setTitle("");
        setPeriod("");
        setExpiresAt("");
        setSignedOnPaper(false);
        setReminderEnabled(false);
        setReminderDays(7);
        if (fileRef.current) fileRef.current.value = "";
    };

    const handleClose = (o) => {
        if (!o) reset();
        onOpenChange(o);
    };

    const handleSubmit = async () => {
        if (!file) {
            toast.error("Seleziona un file");
            return;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("employee_id", employee.id);
        fd.append("category", category);
        if (title) fd.append("title", title);
        if (period) fd.append("period", period);
        if (expiresAt) fd.append("expires_at", expiresAt);
        if (signedOnPaper) fd.append("signed_on_paper", "true");
        fd.append("requires_ack", requireAck && !signedOnPaper ? "true" : "false");
        if (reminderEnabled && Number(reminderDays) > 0) fd.append("reminder_days", String(reminderDays));
        try {
            await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Documento caricato");
            onUploaded && onUploaded();
            handleClose(false);
        } catch (e) {
            const { message, duration } = decodeUploadError(e);
            toast.error(message, { duration });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-lg" data-testid="upload-document-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Carica documento</DialogTitle>
                    <DialogDescription>
                        Carica un singolo documento e assegnalo a {employee.cognome} {employee.nome}.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Categoria</Label>
                        <Select value={category} onValueChange={handleCategoryChange}>
                            <SelectTrigger className="mt-1" data-testid="upload-category">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CATEGORY_ORDER.map((c) => (
                                    <SelectItem key={c} value={c}>
                                        {CATEGORY_LABELS[c]}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                                Titolo (opzionale)
                            </Label>
                            <Input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="es. Busta paga Gennaio 2026"
                                data-testid="upload-title"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                                Periodo YYYY-MM (opzionale)
                            </Label>
                            <Input
                                value={period}
                                onChange={(e) => setPeriod(e.target.value)}
                                placeholder="2026-01"
                                data-testid="upload-period"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Scadenza (opzionale)
                        </Label>
                        <div className="mt-1">
                            <DatePicker
                                value={expiresAt}
                                onChange={setExpiresAt}
                                placeholder="Nessuna scadenza"
                                fromYear={new Date().getFullYear()}
                                toYear={new Date().getFullYear() + 10}
                                data-testid="upload-expires-at"
                            />
                        </div>
                        {CAN_AI_EXPIRY.includes(category) && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={detectExpiry}
                                disabled={aiDetecting || !file}
                                className="mt-2 h-8 gap-1.5 border-violet-200 bg-violet-50 text-xs text-violet-700 hover:bg-violet-100"
                                data-testid="upload-ai-expiry-btn"
                            >
                                {aiDetecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                                {aiDetecting ? "Analisi in corso…" : "Rileva scadenza dal file (AI)"}
                            </Button>
                        )}
                        <p className="mt-1 text-xs text-slate-500">
                            Se valorizzata, riceverai un avviso 10 giorni prima della scadenza.
                        </p>
                    </div>
                    {category === "contratto" && (
                        <label className="flex cursor-pointer items-start gap-2 rounded-md border border-indigo-200 bg-indigo-50/60 p-3 text-sm text-slate-700" data-testid="upload-signed-on-paper-wrap">
                            <input
                                type="checkbox"
                                checked={signedOnPaper}
                                onChange={(e) => setSignedOnPaper(e.target.checked)}
                                className="mt-0.5"
                                data-testid="upload-signed-on-paper"
                            />
                            <span>
                                <span className="font-medium text-indigo-800">Firmato in originale cartaceo</span>
                                <span className="block text-xs text-slate-500">Il file è la versione redatta non scansionata: l'originale firmato è conservato su carta.</span>
                            </span>
                        </label>
                    )}
                    {!signedOnPaper && (
                        <label className="flex cursor-pointer items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-sm text-slate-700" data-testid="upload-require-ack-wrap">
                            <input
                                type="checkbox"
                                checked={requireAck}
                                onChange={(e) => setRequireAck(e.target.checked)}
                                className="mt-0.5"
                                data-testid="upload-require-ack"
                            />
                            <span>
                                <span className="font-medium text-emerald-800">Richiedi presa visione</span>
                                <span className="block text-xs text-slate-500">
                                    Il dipendente dovrà confermare la presa visione del documento (firma elettronica eIDAS).
                                    Preimpostato in base alla categoria.
                                </span>
                            </span>
                        </label>
                    )}
                    <div className="rounded-md border border-sky-200 bg-sky-50/60 p-3" data-testid="upload-reminder-wrap">
                        <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
                            <input
                                type="checkbox"
                                checked={reminderEnabled}
                                onChange={(e) => setReminderEnabled(e.target.checked)}
                                className="mt-0.5"
                                data-testid="upload-reminder-checkbox"
                            />
                            <span>
                                <span className="font-medium text-sky-800">Sollecito automatico di lettura</span>
                                <span className="block text-xs text-slate-500">
                                    Se il dipendente non visualizza il documento, riceverà un promemoria email automatico.
                                </span>
                            </span>
                        </label>
                        {reminderEnabled && (
                            <div className="mt-2 flex items-center gap-2 pl-6 text-sm text-slate-700">
                                <span>Invia dopo</span>
                                <Input
                                    type="number"
                                    min={1}
                                    max={365}
                                    value={reminderDays}
                                    onChange={(e) => setReminderDays(e.target.value)}
                                    className="h-8 w-20"
                                    data-testid="upload-reminder-days"
                                />
                                <span>giorni dal caricamento</span>
                            </div>
                        )}
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">File</Label>
                        <input
                            ref={fileRef}
                            type="file"
                            onChange={(e) => {
                                const f = e.target.files?.[0] || null;
                                setFile(f);
                                if (f && !title) setTitle(cleanTitleClient(f.name, employee));
                            }}
                            className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:text-slate-700 hover:file:bg-slate-200"
                            data-testid="upload-file-input"
                        />
                        {file && (
                            <p className="mt-1 text-xs text-slate-500">{file.name} · {formatBytes(file.size)}</p>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => handleClose(false)} data-testid="upload-cancel">
                        Annulla
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!file || uploading}
                        className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="upload-submit"
                    >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        Carica
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Bulk upload dialog (auto-match per matricola)
// ---------------------------------------------------------------------------
const BulkUploadDialog = ({ open, onOpenChange, onUploaded }) => {
    const [files, setFiles] = useState([]);
    const [category, setCategory] = useState("busta_paga");
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [reminderEnabled, setReminderEnabled] = useState(false);
    const [reminderDays, setReminderDays] = useState(7);
    const [notifyEmails, setNotifyEmails] = useState(true);
    const fileRef = useRef(null);

    const reset = () => {
        setFiles([]);
        setCategory("busta_paga");
        setResult(null);
        setReminderEnabled(false);
        setReminderDays(7);
        setNotifyEmails(true);
        if (fileRef.current) fileRef.current.value = "";
    };

    const handleClose = (o) => {
        if (!o) reset();
        onOpenChange(o);
    };

    const handleSubmit = async () => {
        if (files.length === 0) {
            toast.error("Seleziona i file");
            return;
        }
        setUploading(true);
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        fd.append("category", category);
        fd.append("notify", notifyEmails ? "true" : "false");
        if (reminderEnabled && Number(reminderDays) > 0) fd.append("reminder_days", String(reminderDays));
        try {
            const { data } = await api.post("/documents/bulk", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            if (data.created > 0) {
                toast.success(`${data.created} documenti caricati`);
                onUploaded && onUploaded();
            }
        } catch (e) {
            const { message, duration } = decodeUploadError(e);
            toast.error(message, { duration });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-xl" data-testid="bulk-upload-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Caricamento massivo</DialogTitle>
                    <DialogDescription>
                        Carica più file con assegnazione automatica via matricola nel nome file. Formato consigliato: <code className="rounded bg-slate-100 px-1">matricola_yyyy-mm.pdf</code> (es. <code className="rounded bg-slate-100 px-1">1388_2026-01.pdf</code>).
                    </DialogDescription>
                </DialogHeader>
                {!result ? (
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                                Categoria per tutti i file
                            </Label>
                            <Select value={category} onValueChange={setCategory}>
                                <SelectTrigger className="mt-1" data-testid="bulk-category">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {CATEGORY_ORDER.map((c) => (
                                        <SelectItem key={c} value={c}>
                                            {CATEGORY_LABELS[c]}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <label className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid="bulk-notify-wrap">
                            <input
                                type="checkbox"
                                checked={notifyEmails}
                                onChange={(e) => setNotifyEmails(e.target.checked)}
                                className="mt-0.5"
                                data-testid="bulk-notify-checkbox"
                            />
                            <span>
                                <span className="font-medium text-slate-800">Invia email di notifica ai dipendenti</span>
                                <span className="block text-xs text-slate-500">Ogni dipendente riceve l'email "nuovo documento disponibile" per i file assegnati</span>
                            </span>
                        </label>
                        <div className="rounded-md border border-sky-200 bg-sky-50/60 p-3" data-testid="bulk-reminder-wrap">
                            <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
                                <input
                                    type="checkbox"
                                    checked={reminderEnabled}
                                    onChange={(e) => setReminderEnabled(e.target.checked)}
                                    className="mt-0.5"
                                    data-testid="bulk-reminder-checkbox"
                                />
                                <span>
                                    <span className="font-medium text-sky-800">Sollecito automatico di lettura</span>
                                    <span className="block text-xs text-slate-500">
                                        Chi non visualizza il documento riceverà un promemoria email automatico.
                                    </span>
                                </span>
                            </label>
                            {reminderEnabled && (
                                <div className="mt-2 flex items-center gap-2 pl-6 text-sm text-slate-700">
                                    <span>Invia dopo</span>
                                    <Input
                                        type="number"
                                        min={1}
                                        max={365}
                                        value={reminderDays}
                                        onChange={(e) => setReminderDays(e.target.value)}
                                        className="h-8 w-20"
                                        data-testid="bulk-reminder-days"
                                    />
                                    <span>giorni dal caricamento</span>
                                </div>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                                Files
                            </Label>
                            <input
                                ref={fileRef}
                                type="file"
                                multiple
                                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                                className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:text-slate-700 hover:file:bg-slate-200"
                                data-testid="bulk-file-input"
                            />
                            {files.length > 0 && (
                                <div className="mt-2 max-h-32 overflow-y-auto rounded-md bg-slate-50 p-2 text-xs text-slate-600">
                                    {files.length} file selezionati · totale {formatBytes(files.reduce((s, f) => s + f.size, 0))}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="bulk-result">
                        <div className="flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                            <div>
                                <div className="text-sm font-semibold text-emerald-900">
                                    {result.created} documenti caricati
                                </div>
                                {result.skipped > 0 && (
                                    <div className="text-xs text-emerald-700">{result.skipped} saltati</div>
                                )}
                                {result.duplicates > 0 && (
                                    <div className="text-xs text-emerald-700" data-testid="bulk-duplicates-count">{result.duplicates} duplicati ignorati (già in archivio)</div>
                                )}
                            </div>
                        </div>
                        {result.errors && result.errors.length > 0 && (
                            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                                <div className="text-sm font-semibold text-amber-900">Errori ({result.errors.length})</div>
                                <div className="mt-2 max-h-48 overflow-y-auto text-xs text-amber-800">
                                    {result.errors.map((err, i) => (
                                        <div key={`${err.filename}-${i}`} className="border-b border-amber-200 py-1 last:border-b-0">
                                            <span className="font-semibold">{err.filename}:</span> {err.error}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    {result ? (
                        <>
                            <Button variant="outline" onClick={reset}>Nuovo caricamento</Button>
                            <Button onClick={() => handleClose(false)} className="bg-[#376DB3] text-white hover:bg-[#376DB3]/90">
                                Chiudi
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => handleClose(false)}>Annulla</Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={files.length === 0 || uploading}
                                className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                data-testid="bulk-submit"
                            >
                                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                Carica {files.length > 0 && `(${files.length})`}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Credentials dialog (set login email + password for the employee)
// ---------------------------------------------------------------------------
const CredentialsDialog = ({ open, onOpenChange, employee }) => {
    const [email, setEmail] = useState(employee.email || "");
    const [password, setPassword] = useState("");
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [generated, setGenerated] = useState(null);

    useEffect(() => {
        if (open) {
            setEmail(employee.email || "");
            setPassword("");
            setGenerated(null);
        }
    }, [open, employee.email]);

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post(`/employees/${employee.id}/generate-credentials`);
            setGenerated(data);
            toast.success("Credenziali standard generate");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore generazione");
        } finally {
            setGenerating(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const body = {};
            if (email) body.email = email.trim();
            if (password) body.password = password;
            if (!body.email && !body.password) {
                toast.error("Inserisci almeno email o password");
                setSaving(false);
                return;
            }
            await api.post(`/employees/${employee.id}/credentials`, body);
            toast.success("Credenziali aggiornate");
            onOpenChange(false);
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="credentials-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Credenziali Portale</DialogTitle>
                    <DialogDescription>
                        Imposta email e password per consentire a {employee.cognome} {employee.nome} di accedere al portale e scaricare i suoi documenti.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Email</Label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="nome.cognome@splendorsud.it"
                            data-testid="cred-email"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Nuova Password {!password && employee.email && <span className="font-normal text-slate-500">(lascia vuoto per non cambiarla)</span>}
                        </Label>
                        <Input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Min 6 caratteri"
                            autoComplete="new-password"
                            data-testid="cred-password"
                        />
                    </div>
                    <div className="rounded-md border border-dashed border-slate-300 bg-slate-50/60 p-3">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleGenerate}
                            disabled={generating}
                            className="gap-2"
                            data-testid="cred-generate-btn"
                            title="Utente = email o cognome (omonimi: cognome2, 3…) · Password = Nome+matricola"
                        >
                            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                            Genera credenziali standard
                        </Button>
                        <p className="mt-1.5 text-[11px] text-slate-500">
                            Utente = email o cognome (omonimi: cognome2, 3…) · Password = Nome+matricola
                        </p>
                        {generated && (
                            <div className="mt-2 space-y-1 rounded-md bg-white p-2.5 text-sm shadow-sm" data-testid="cred-generated-box">
                                <div className="flex items-center justify-between gap-2">
                                    <span>Utente: <b className="font-mono-numbers">{generated.userid}</b></span>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                    <span>Password: <b className="font-mono-numbers">{generated.password}</b></span>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={() => {
                                            navigator.clipboard.writeText(`Utente: ${generated.userid}\nPassword: ${generated.password}`);
                                            toast.success("Copiato negli appunti");
                                        }}
                                        data-testid="cred-copy-btn"
                                    >
                                        <Copy className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                                <p className="text-[11px] text-amber-700">Annota la password ora: non sarà più visibile.</p>
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="cred-save"
                    >
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Salva
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};


// ---------------------------------------------------------------------------
// Reassign document to another employee
// ---------------------------------------------------------------------------
const ReassignDocumentDialog = ({ doc, onClose, onReassigned }) => {
    const [search, setSearch] = useState("");
    const [employees, setEmployees] = useState([]);
    const [selected, setSelected] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!doc) return;
        (async () => {
            try {
                const { data } = await api.get("/employees");
                setEmployees(data);
            } catch {
                /* silent */
            }
        })();
    }, [doc]);

    const filtered = (() => {
        const q = search.trim().toLowerCase();
        if (!q) return employees.slice(0, 50);
        return employees.filter((e) =>
            (e.cognome || "").toLowerCase().includes(q) ||
            (e.nome || "").toLowerCase().includes(q) ||
            (e.matricola || "").toLowerCase().includes(q)
        ).slice(0, 50);
    })();

    const handleConfirm = async () => {
        if (!selected) return;
        setSaving(true);
        try {
            await api.patch(`/documents/${doc.id}`, { employee_id: selected });
            const target = employees.find((e) => e.id === selected);
            toast.success(`Documento riassegnato a ${target?.cognome || ""} ${target?.nome || ""}`);
            setSelected("");
            setSearch("");
            onReassigned();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore durante la riassegnazione");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!doc} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="reassign-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <ArrowRightLeft className="h-5 w-5 text-amber-600" />
                        Riassegna documento
                    </DialogTitle>
                    <DialogDescription className="truncate font-mono text-xs">
                        {doc?.filename}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Cerca per cognome, nome o matricola…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                            data-testid="reassign-search"
                            autoFocus
                        />
                    </div>
                    <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200" data-testid="reassign-list">
                        {filtered.map((e) => {
                            const isSelected = selected === e.id;
                            const isCessato = !!e.data_cessazione;
                            return (
                                <button
                                    type="button"
                                    key={e.id}
                                    onClick={() => setSelected(e.id)}
                                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 ${isSelected ? "bg-[#376DB3]/10" : ""}`}
                                    data-testid={`reassign-option-${e.id}`}
                                >
                                    <span>
                                        <b>{e.cognome} {e.nome}</b>
                                        <span className="ml-2 text-xs text-slate-500">matr. {e.matricola}</span>
                                        {isCessato && <span className="ml-2 text-xs font-medium text-amber-600">CESSATO</span>}
                                    </span>
                                    {isSelected && <CheckCircle2 className="h-4 w-4 text-[#376DB3]" />}
                                </button>
                            );
                        })}
                        {filtered.length === 0 && (
                            <div className="px-3 py-6 text-center text-sm text-slate-500">Nessun dipendente trovato</div>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="reassign-cancel">Annulla</Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={!selected || saving}
                        className="gap-2 bg-amber-600 text-white hover:bg-amber-700"
                        data-testid="reassign-confirm"
                    >
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Conferma riassegnazione
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};


// ---------------------------------------------------------------------------
// Download history dialog (admin) — audit view per document
// ---------------------------------------------------------------------------
const DownloadHistoryDialog = ({ doc, employee, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [downloads, setDownloads] = useState([]);
    const company = useCompany();
    const [generatingId, setGeneratingId] = useState(null);

    useEffect(() => {
        if (!doc) return;
        setLoading(true);
        api.get(`/delivery/documents/${doc.id}/downloads`).then((r) => {
            setDownloads(r.data.downloads || []);
        }).catch(() => {
            setDownloads([]);
        }).finally(() => setLoading(false));
    }, [doc]);

    const handleGenerateReceipt = async (dl) => {
        setGeneratingId(dl.id);
        try {
            const settings = await loadSettings();
            await generateDeliveryReceiptPDF({
                download: dl,
                employee,
                document: doc,
                company,
                firmaDatoreUrl: settings?.firma_datore || null,
                logoUrl: settings?.logo || null,
            });
            toast.success("Ricevuta PDF generata");
        } catch (e) {
            toast.error("Errore generazione ricevuta");
        } finally {
            setGeneratingId(null);
        }
    };

    return (
        <Dialog open={!!doc} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-3xl" data-testid="download-history-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <History className="h-5 w-5 text-violet-600" />
                        Storico download
                    </DialogTitle>
                    <DialogDescription className="truncate font-mono text-xs">
                        {doc?.filename}
                    </DialogDescription>
                </DialogHeader>

                <div className="max-h-[60vh] overflow-y-auto">
                    {loading ? (
                        <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>
                    ) : downloads.length === 0 ? (
                        <div className="py-10 text-center text-sm text-slate-500">
                            Nessun download registrato per questo documento.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {downloads.map((dl) => (
                                <div key={dl.id} className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs" data-testid={`history-row-${dl.id}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="font-mono-numbers font-medium text-slate-700">
                                            {formatDateTime(dl.downloaded_at)}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {dl.acknowledged ? (
                                                <Badge variant="outline" className="gap-1 border-emerald-300 bg-emerald-50 text-emerald-700">
                                                    <CheckCircle2 className="h-3 w-3" />
                                                    Presa visione confermata
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="gap-1 border-slate-300 bg-white text-slate-600">
                                                    Solo download
                                                </Badge>
                                            )}
                                            {dl.via === "office_ack" && (
                                                <Badge variant="outline" className="gap-1 border-indigo-300 bg-indigo-50 text-indigo-700" data-testid={`history-office-badge-${dl.id}`} title="Presa visione registrata dall'ufficio, non dal dipendente">
                                                    Registrata d'ufficio
                                                </Badge>
                                            )}
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleGenerateReceipt(dl)}
                                                disabled={generatingId === dl.id}
                                                className="h-7 gap-1 border-violet-300 bg-white text-xs text-violet-700 hover:bg-violet-50"
                                                data-testid={`history-receipt-${dl.id}`}
                                                title="Scarica ricevuta PDF di consegna"
                                            >
                                                {generatingId === dl.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileDown className="h-3 w-3" />}
                                                Ricevuta PDF
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="mt-1 grid gap-1 sm:grid-cols-2">
                                        <div>
                                            <span className="font-medium text-slate-500">Operatore:</span>{" "}
                                            <span className="text-slate-700">{dl.downloaded_by_email || "—"}</span>
                                            <span className="ml-1 text-slate-400">({dl.downloaded_by_role || "?"})</span>
                                        </div>
                                        <div>
                                            <span className="font-medium text-slate-500">IP:</span>{" "}
                                            <span className="font-mono text-slate-700">{dl.ip || "—"}</span>
                                        </div>
                                        <div className="sm:col-span-2 truncate">
                                            <span className="font-medium text-slate-500">User-Agent:</span>{" "}
                                            <span className="font-mono text-[10px] text-slate-600">{dl.user_agent || "—"}</span>
                                        </div>
                                        <div className="sm:col-span-2 truncate">
                                            <span className="font-medium text-slate-500">SHA-256 file:</span>{" "}
                                            <span className="font-mono text-[10px] text-slate-600">{dl.file_sha256 || "—"}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button onClick={onClose} className="bg-[#376DB3] text-white hover:bg-[#376DB3]/90" data-testid="history-close-btn">
                        Chiudi
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
